import { html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ifDefined } from 'lit-html/directives/if-defined';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/selectors';
import actions from '@elab/core/store/actions';
import animationHelpers from '@elab/core/utils/ui/animationHelpers';
import { getValueByStorePath } from '@elab/core/utils/helpers';
import getSelectors from '@elab/core/utils/dynaForms/selectors';
import { cacheSelector, compareState } from '@elab/core/utils/webComponent';

import { FormElement } from './Form';

function render() {
    const modifiers = this.getModifiers();

    const labelClassName = `toggler__label-block ${
        this.disabled ? 'toggler__label--disabled' : null
    }`;
    const contentClassName = `toggler__content ${
        this.showContent() ? 'toggler__content--visible' : ''
    }`;

    const clickHandler = evt => {
        const linkEvent = evt.target.closest('.toggler__label-block a');
        const compEvent = evt.target.closest('.toggler__label-block v-root');

        if (!linkEvent || compEvent) {
            evt.preventDefault();
        }

        if (!linkEvent && !compEvent && !this.disabled) {
            this.handleChange();
        }
    };

    const changeHandler = evt => {
        evt.stopPropagation();
        return false;
    };

    const renderAsCheckboxOrToggler = () => {
        return html`
            <div class="toggler__wrapper ${this.cssClass}">
                <div class="toggler ${modifiers}">
                    <label class="${labelClassName}" @click="${clickHandler}">
                        <input
                            class="toggler__input"
                            type="checkbox"
                            ?disabled=${this.disabled}
                            .checked="${this.isChecked()}"
                            data-gtm-label="${ifDefined(this.gtmLabel || undefined)}"
                        />
                        ${this.displayAsCheckbox
                            ? renderAsCheckbox()
                            : renderAsToggler(this.captionYes, this.captionNo)}
                        ${getLabelSlot(!this.withoutLabel)}
                    </label>

                    ${getLabelSlot(this.withoutLabel)}
                    <v-slot name="tooltip"></v-slot>
                </div>

                <div class="${contentClassName}" @change="${changeHandler}">
                    <v-slot name="toggler__content"></v-slot>
                </div>
                <div class="toggler__error">
                    <v-slot name="error"></v-slot>
                </div>
            </div>
        `;
    };

    return this.analyticsConfig !== ''
        ? html`
              <e2-click-tracking analytics-config="${this.analyticsConfig}" type="Payment-Terms"
                  >${renderAsCheckboxOrToggler()}</e2-click-tracking
              >
          `
        : renderAsCheckboxOrToggler();
}

function renderAsCheckbox() {
    return html`
        <div class="toggler__icon-wrapper">
            <i class="toggler__icon icon icon-tick-isolated"></i>
        </div>
    `;
}

function renderAsToggler(captionYes, captionNo) {
    return html`
        <div class="toggler__display">
            <div class="toggler__caption">
                <span class="toggler__caption-yes">${captionYes}</span>
                <span class="toggler__caption-no">${captionNo}</span>
            </div>
        </div>
    `;
}

function getModifiers() {
    return classMap({
        [`toggler--${this.align}`]: true,
        [`toggler--align-items-${this.alignItems}`]: this.alignItems,
        'toggler--display-as-checkbox': this.displayAsCheckbox,
        [`toggler--label-${this.labelStyle}`]: this.labelStyle,
        'toggler--error': this.hasError(),
    });
}

function getLabelSlot(showSlot) {
    return showSlot
        ? html`
              <div class="toggler__label">
                  <v-slot name="toggler__label"></v-slot>
              </div>
          `
        : html``;
}

const PROPERTIES = {
    align: { type: String },
    checked: { type: Boolean },
    inverted: { type: Boolean },
    alignItems: { type: String, attribute: 'align-items' },
    captionYes: { type: String, attribute: 'caption-yes' },
    captionNo: { type: String, attribute: 'caption-no' },
    cssClass: { type: String, attribute: 'css-class' },
    withoutLabel: { type: Boolean, attribute: 'without-label' },
    labelStyle: { type: String, attribute: 'label-style' },
    name: { type: String },
    disabled: { type: Boolean },
    displayAsCheckbox: { type: Boolean, attribute: 'display-as-checkbox' },
    isError: { type: Boolean, attribute: false },
    required: { type: Boolean, reflect: true },
    analyticsConfig: { type: String, attribute: 'analytics-config' },
    gtmLabel: {
        type: String,
        attribute: 'gtm-label',
    },
};

export class Toggler extends LightLitElement {
    static get properties() {
        return PROPERTIES;
    }

    constructor() {
        super();

        this.render = render.bind(this);
        this.getModifiers = getModifiers.bind(this);
        this.checked = false;
        this.inverted = false;
        this.disabled = false;
        this.isError = false;
        this.required = false;
        this.cssClass = '';
        this.analyticsConfig = '';
        this.gtmLabel = '';
    }

    isChecked() {
        return this.checked;
    }

    showContent() {
        return this.inverted ? !this.isChecked() : this.isChecked();
    }

    toggleStatus() {
        this.checked = !this.checked;
    }

    handleChange() {
        this.toggleStatus();
        this.dispatchEvent(
            new CustomEvent('change', {
                detail: { checked: this.isChecked() },
            }),
        );
    }

    hasError() {
        return this.isError;
    }
}

export class FormToggler extends FormElement {
    static get properties() {
        return PROPERTIES;
    }

    constructor() {
        super();

        this.render = render.bind(this);
        this.getModifiers = getModifiers.bind(this);
        this.checked = false;
        this.inverted = false;
        this.disabled = false;
        this.isError = false;
        this.required = false;
        this.analyticsConfig = '';
        this.cssClass = '';
        this.gtmLabel = '';
        this.analyticsConfig = '';
    }

    shouldUpdate(changedProps) {
        return [
            ['align'],
            ['checked'],
            ['inverted'],
            ['disabled'],
            ['isError'],
            ['required'],
        ].some(item => compareState(this, changedProps, ...item));
    }

    firstUpdated() {
        this.content = this.querySelector('.toggler__content');
        this.getContent().addEventListener('transitionend', this.handleTransitionEnd);
    }

    disconnectedCallback() {
        if (this.content) {
            this.content.removeEventListener('transitionend', this.handleTransitionEnd);
        }

        super.disconnectedCallback();
    }

    handleTransitionEnd = () =>
        animationHelpers.transitionEndedOnHeightHandler(this.getContent(), () =>
            this.changeValue(),
        );

    getContent() {
        return this.content;
    }

    showContent() {
        return this.inverted ? !this.isChecked() : this.isChecked();
    }

    toggleStatus() {
        this.checked = !this.checked;
    }

    isChecked() {
        return this.checked;
    }

    hasError() {
        return this.isError;
    }

    handleChange() {
        this.toggleStatus();
        animationHelpers.handleHeightForAnimation(this.getContent());
        this.changeValue(this.isChecked());
    }
}

export class ConnectedToggler extends Toggler {
    static get properties() {
        return {
            storeName: { type: String, attribute: 'store-name' },
            storeField: { type: String, attribute: 'store-field' },
            storeError: { type: String, attribute: 'store-error' },
            storeAction: { type: String, attribute: 'store-action' },
        };
    }

    constructor() {
        super();
        this.changeEventId = `change-${this.storeField}`;
        this.checked = false;
        this.inverted = false;
        this.disabled = false;
        this.isError = false;
        this.required = false;
    }

    firstUpdated() {
        this.content = this.querySelector('.toggler__content');
        this.getContent().addEventListener('transitionend', this.handleTransitionEnd);
    }

    disconnectedCallback() {
        if (this.content) {
            this.content.removeEventListener('transitionend', this.handleTransitionEnd);
        }
    }

    handleTransitionEnd = () => animationHelpers.transitionEndedOnHeightHandler(this.getContent());

    handleChange() {
        animationHelpers.handleHeightForAnimation(this.getContent());
        this.dispatchEvent(new CustomEvent(this.changeEventId));
    }

    getContent() {
        return this.content;
    }
}

const mapStateToProps = (state, ctx) => {
    return {
        checked: selectors.getFieldOrDefault(state, ctx.storeName, ctx.storeField),
        isError: ctx.storeError && getValueByStorePath(`${ctx.storeName}.${ctx.storeError}`, state),
    };
};

const mapStateToFormProps = (state, ctx) => {
    const selectors = cacheSelector(ctx, getSelectors);

    return {
        checked: selectors.getDataFieldValue(state, ctx.name),
        isError: selectors.getDataFieldError(state, ctx.name),
        required: selectors.isDataFieldRequired(state, ctx.name),
        disabled: selectors.getDataFieldProperty(state, ctx.name, 'readOnly'),
    };
};

const mapDispatchToEvents = (dispatch, ctx) => ({
    [ctx.changeEventId]: () =>
        dispatch(
            actions.changeDataFieldValue(ctx.storeName, ctx.storeAction || 'TOGGLE_FIELD_VALUE', {
                name: ctx.storeField,
                value: !ctx.checked,
            }),
        ),
});

export default () => {
    customElements.define('e2-toggler', Toggler);
    customElements.define(
        'e2-connected-toggler',
        connect(mapStateToProps, mapDispatchToEvents)(ConnectedToggler),
    );
    customElements.define('e2-readonly-toggler', connect(mapStateToProps)(ConnectedToggler));
    customElements.define('e2-form-toggler', connect(mapStateToFormProps)(FormToggler));
};
